import { translateLabel } from "@/i18n/messages";
const DAY_MS = 86_400_000;
export const STATUS_STALE_AFTER_DAYS = 7;

const parsedDate = (value) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp) : null;
};

export const operationalStatusMeta = (status) => {
  switch (String(status || "UNKNOWN").toUpperCase()) {
    case "OPEN":
      return { status: "OPEN", label: translateLabel("Open"), tone: "open", warning: false };
    case "CLOSED":
      return { status: "CLOSED", label: translateLabel("Closed"), tone: "closed", warning: true };
    case "RESTRICTED":
      return { status: "RESTRICTED", label: translateLabel("Restricted"), tone: "restricted", warning: true };
    default:
      return { status: "UNKNOWN", label: translateLabel("Status unknown"), tone: "unknown", warning: true };
  }
};

export const verificationFreshness = (
  verification,
  now = new Date(),
  staleAfterDays = STATUS_STALE_AFTER_DAYS,
) => {
  const checkedAt = parsedDate(verification?.checked_at);
  if (!checkedAt) {
    return {
      state: "UNKNOWN",
      label: translateLabel("Not recently verified"),
      checkedAt: null,
      ageDays: null,
      stale: true,
    };
  }

  const ageMs = Math.max(0, now.getTime() - checkedAt.getTime());
  const ageDays = Math.floor(ageMs / DAY_MS);
  if (ageDays === 0) {
    return {
      state: "CURRENT",
      label: translateLabel("Verified today"),
      checkedAt,
      ageDays: 0,
      stale: false,
    };
  }
  if (ageDays <= staleAfterDays) {
    return {
      state: "CURRENT",
      label: translateLabel(ageDays === 1 ? "Checked {count} day ago" : "Checked {count} days ago", { count: ageDays }),
      checkedAt,
      ageDays,
      stale: false,
    };
  }

  return {
    state: "STALE",
    label: translateLabel("Not recently verified"),
    checkedAt,
    ageDays,
    stale: true,
  };
};

export const statusTrustSignal = (status, verification, now = new Date()) => {
  const operational = operationalStatusMeta(status);
  const freshness = verificationFreshness(verification, now);
  const sourceUrl = verification?.source?.url || null;
  const sourceLabel = verification?.source?.label || (sourceUrl ? translateLabel("Official source") : null);

  let warning = null;
  if (operational.status === "UNKNOWN") {
    warning = translateLabel("Current operational status is not verified.");
  } else if (freshness.state === "STALE") {
    warning = translateLabel("This status may be outdated. Check the official source before travelling.");
  } else if (freshness.state === "UNKNOWN") {
    warning = translateLabel("Verification date is unavailable. Check before travelling.");
  } else if (operational.status === "CLOSED") {
    warning = translateLabel("Currently marked closed.");
  } else if (operational.status === "RESTRICTED") {
    warning = translateLabel("Access or operation is currently restricted.");
  }

  return {
    ...operational,
    freshness,
    sourceUrl,
    sourceLabel,
    warning,
    uncertain: operational.status === "UNKNOWN" || freshness.stale,
  };
};

export const conciseStatusWarning = (status, verification, now = new Date()) => {
  const signal = statusTrustSignal(status, verification, now);
  if (signal.status === "CLOSED") return translateLabel("Closed");
  if (signal.status === "RESTRICTED") return translateLabel("Restricted");
  if (signal.status === "UNKNOWN") return translateLabel("Status unknown");
  if (signal.freshness.state === "STALE") return translateLabel("Status not recently verified");
  if (signal.freshness.state === "UNKNOWN") return translateLabel("Status verification unavailable");
  return null;
};
